/**
 * Story Wave Generator
 * Gerador de ondas específico para o Modo História com suporte a bosses
 */

import * as THREE from "three";
import { TankData, ObstacleData } from "../stores/useRocketanks";
import { 
  WaveConfig, 
  EnemyConfig, 
  TerrainConfig, 
  TERRAIN_THEMES, 
  ArenaShape,
  generateArenaObstacles 
} from "../waveGenerator";

// Tipos de boss por capítulo
export interface BossConfig {
  id: string;
  name: string;
  title: string;
  health: number;
  speed: number;
  damage: number;
  fireRate: number;
  shotCount: number;
  size: number;
  color: string;
  aiLevel: number;
  phases: number;
  phaseConfigs?: PhaseConfig[];
  specialAbility?: "mines" | "freeze_paths" | "fog" | null;
}

export interface PhaseConfig {
  health: number;
  damage: number;
  shotCount: number;
  speed: number;
  pattern?: "normal" | "erratic";
}

export interface StoryWaveConfig {
  chapterId: number;
  waveIndex: number;
  enemyCount: number;
  isBossWave: boolean;
  bossConfig?: BossConfig;
  triggerDialogue?: string | null;
}

// Configuração de boss por capítulo
export const BOSS_CONFIGS: Record<number, BossConfig> = {
  2: {
    id: "boss-rok",
    name: "Sargento RØK",
    title: "O Cão de Aço",
    health: 400,
    speed: 6,
    damage: 35,
    fireRate: 1800,
    shotCount: 2,
    size: 1.6,
    color: "#8B0000",
    aiLevel: 0.7,
    phases: 1,
    specialAbility: null,
  },
  3: {
    id: "boss-nyx",
    name: "Capitã Nyx",
    title: "A Corsária de Neon",
    health: 500,
    speed: 8,
    damage: 30,
    fireRate: 1500,
    shotCount: 3,
    size: 1.5,
    color: "#FFD700",
    aiLevel: 0.75,
    phases: 1,
    specialAbility: "mines",
  },
  4: {
    id: "boss-boreal",
    name: "Comandante Boreal",
    title: "O Lobo do Gelo",
    health: 550,
    speed: 5,
    damage: 45,
    fireRate: 2000,
    shotCount: 2,
    size: 1.8,
    color: "#00BFFF",
    aiLevel: 0.8,
    phases: 1,
    specialAbility: "freeze_paths",
  },
  5: {
    id: "boss-verdant",
    name: "General Verdant",
    title: "A Serpente da Floresta",
    health: 600,
    speed: 7,
    damage: 40,
    fireRate: 1600,
    shotCount: 3,
    size: 1.7,
    color: "#228B22",
    aiLevel: 0.85,
    phases: 1,
    specialAbility: "fog",
  },
  6: {
    id: "boss-titan",
    name: "TITÃ-AXIS",
    title: "O Coração de Arcádia",
    health: 1200,
    speed: 4,
    damage: 50,
    fireRate: 1400,
    shotCount: 3,
    size: 2.2,
    color: "#FF4500",
    aiLevel: 0.95,
    phases: 3,
    phaseConfigs: [
      { health: 400, damage: 50, shotCount: 3, speed: 4, pattern: "normal" },
      { health: 400, damage: 60, shotCount: 4, speed: 5, pattern: "normal" },
      { health: 400, damage: 70, shotCount: 5, speed: 6, pattern: "erratic" },
    ],
    specialAbility: null,
  },
  7: {
    id: "boss-echo",
    name: "ECO-ZERO",
    title: "O Primeiro Protótipo",
    health: 700,
    speed: 7,
    damage: 40,
    fireRate: 1500,
    shotCount: 3,
    size: 1.6,
    color: "#a855f7",
    aiLevel: 0.85,
    phases: 2,
    phaseConfigs: [
      { health: 350, damage: 40, shotCount: 3, speed: 7, pattern: "normal" },
      { health: 350, damage: 55, shotCount: 4, speed: 8, pattern: "erratic" },
    ],
    specialAbility: "fog",
  },
  8: {
    id: "boss-glacier",
    name: "GELO-MESTRE",
    title: "A Sentinela Polar",
    health: 800,
    speed: 4,
    damage: 50,
    fireRate: 1800,
    shotCount: 3,
    size: 1.9,
    color: "#60a5fa",
    aiLevel: 0.88,
    phases: 2,
    phaseConfigs: [
      { health: 400, damage: 50, shotCount: 3, speed: 4, pattern: "normal" },
      { health: 400, damage: 65, shotCount: 5, speed: 6, pattern: "erratic" },
    ],
    specialAbility: "freeze_paths",
  },
  9: {
    id: "boss-corvus",
    name: "CORVUS-7",
    title: "O Traidor Sombrio",
    health: 1100,
    speed: 8,
    damage: 55,
    fireRate: 1300,
    shotCount: 4,
    size: 1.9,
    color: "#1e1b4b",
    aiLevel: 0.95,
    phases: 3,
    phaseConfigs: [
      { health: 360, damage: 55, shotCount: 4, speed: 8, pattern: "normal" },
      { health: 370, damage: 65, shotCount: 5, speed: 9, pattern: "erratic" },
      { health: 370, damage: 75, shotCount: 6, speed: 10, pattern: "erratic" },
    ],
    specialAbility: "mines",
  },
  10: {
    id: "boss-kitsune-prime",
    name: "KITSUNE-PRIME",
    title: "A Origem Despertada",
    health: 1500,
    speed: 6,
    damage: 60,
    fireRate: 1200,
    shotCount: 5,
    size: 2.4,
    color: "#f43f5e",
    aiLevel: 1.0,
    phases: 3,
    phaseConfigs: [
      { health: 500, damage: 60, shotCount: 5, speed: 6, pattern: "normal" },
      { health: 500, damage: 75, shotCount: 6, speed: 7, pattern: "erratic" },
      { health: 500, damage: 95, shotCount: 8, speed: 9, pattern: "erratic" },
    ],
    specialAbility: "fog",
  },
};

// Configuração de terreno por capítulo
export const CHAPTER_TERRAIN_MAP: Record<number, keyof typeof TERRAIN_THEMES> = {
  1: "industrial",
  2: "industrial",
  3: "desert",
  4: "arctic",
  5: "forest",
  6: "volcanic",
  7: "cyber",
  8: "arctic",
  9: "cyber",
  10: "volcanic",
};

// Configuração de ondas por capítulo
export const CHAPTER_WAVES: Record<number, StoryWaveConfig[]> = {
  1: [
    // Mini-dungeon tutorial: 4 salas com waves progressivas
    { chapterId: 1, waveIndex: 0, enemyCount: 2, isBossWave: false, triggerDialogue: "chapter1_tutorial_movement" },
    { chapterId: 1, waveIndex: 1, enemyCount: 4, isBossWave: false, triggerDialogue: "chapter1_tutorial_dodge" },
    { chapterId: 1, waveIndex: 2, enemyCount: 0, isBossWave: false, triggerDialogue: "chapter1_nen_encouragement" }, // Corredor sem combate
    { chapterId: 1, waveIndex: 3, enemyCount: 5, isBossWave: false, triggerDialogue: "chapter1_final_wave" },
  ],
  2: [
    { chapterId: 2, waveIndex: 0, enemyCount: 3, isBossWave: false, triggerDialogue: null },
    { chapterId: 2, waveIndex: 1, enemyCount: 4, isBossWave: false, triggerDialogue: "wave_2_intro" },
    { chapterId: 2, waveIndex: 2, enemyCount: 5, isBossWave: false, triggerDialogue: "wave_3_intro" },
    { chapterId: 2, waveIndex: 3, enemyCount: 0, isBossWave: true, bossConfig: BOSS_CONFIGS[2], triggerDialogue: "boss_intro" },
  ],
  3: [
    { chapterId: 3, waveIndex: 0, enemyCount: 4, isBossWave: false, triggerDialogue: null },
    { chapterId: 3, waveIndex: 1, enemyCount: 5, isBossWave: false, triggerDialogue: null },
    { chapterId: 3, waveIndex: 2, enemyCount: 0, isBossWave: true, bossConfig: BOSS_CONFIGS[3], triggerDialogue: "boss_intro" },
  ],
  4: [
    { chapterId: 4, waveIndex: 0, enemyCount: 4, isBossWave: false, triggerDialogue: null },
    { chapterId: 4, waveIndex: 1, enemyCount: 5, isBossWave: false, triggerDialogue: null },
    { chapterId: 4, waveIndex: 2, enemyCount: 0, isBossWave: true, bossConfig: BOSS_CONFIGS[4], triggerDialogue: "boss_intro" },
  ],
  5: [
    { chapterId: 5, waveIndex: 0, enemyCount: 5, isBossWave: false, triggerDialogue: null },
    { chapterId: 5, waveIndex: 1, enemyCount: 6, isBossWave: false, triggerDialogue: null },
    { chapterId: 5, waveIndex: 2, enemyCount: 0, isBossWave: true, bossConfig: BOSS_CONFIGS[5], triggerDialogue: "boss_intro" },
  ],
  6: [
    { chapterId: 6, waveIndex: 0, enemyCount: 3, isBossWave: false, triggerDialogue: null },
    { chapterId: 6, waveIndex: 1, enemyCount: 0, isBossWave: true, bossConfig: BOSS_CONFIGS[6], triggerDialogue: "boss_intro" },
  ],
  7: [
    { chapterId: 7, waveIndex: 0, enemyCount: 4, isBossWave: false, triggerDialogue: null },
    { chapterId: 7, waveIndex: 1, enemyCount: 5, isBossWave: false, triggerDialogue: null },
    { chapterId: 7, waveIndex: 2, enemyCount: 6, isBossWave: false, triggerDialogue: null },
    { chapterId: 7, waveIndex: 3, enemyCount: 0, isBossWave: true, bossConfig: BOSS_CONFIGS[7], triggerDialogue: "boss_intro" },
  ],
  8: [
    { chapterId: 8, waveIndex: 0, enemyCount: 5, isBossWave: false, triggerDialogue: null },
    { chapterId: 8, waveIndex: 1, enemyCount: 6, isBossWave: false, triggerDialogue: null },
    { chapterId: 8, waveIndex: 2, enemyCount: 7, isBossWave: false, triggerDialogue: null },
    { chapterId: 8, waveIndex: 3, enemyCount: 0, isBossWave: true, bossConfig: BOSS_CONFIGS[8], triggerDialogue: "boss_intro" },
  ],
  9: [
    { chapterId: 9, waveIndex: 0, enemyCount: 5, isBossWave: false, triggerDialogue: null },
    { chapterId: 9, waveIndex: 1, enemyCount: 6, isBossWave: false, triggerDialogue: null },
    { chapterId: 9, waveIndex: 2, enemyCount: 8, isBossWave: false, triggerDialogue: null },
    { chapterId: 9, waveIndex: 3, enemyCount: 0, isBossWave: true, bossConfig: BOSS_CONFIGS[9], triggerDialogue: "boss_intro" },
  ],
  10: [
    { chapterId: 10, waveIndex: 0, enemyCount: 6, isBossWave: false, triggerDialogue: null },
    { chapterId: 10, waveIndex: 1, enemyCount: 8, isBossWave: false, triggerDialogue: null },
    { chapterId: 10, waveIndex: 2, enemyCount: 0, isBossWave: true, bossConfig: BOSS_CONFIGS[10], triggerDialogue: "boss_intro" },
  ],
};

// Gerador de seed baseado em capítulo
function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

function deterministicSeed(...parts: Array<string | number>): number {
  const key = parts.map((part) => String(part)).join("|");
  let hash = 2166136261;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  const normalized = hash & 0x7fffffff;
  return normalized === 0 ? 1 : normalized;
}

// Gerar configuração de arena para um capítulo
export function generateStoryArenaConfig(chapterId: number): WaveConfig {
  const terrainKey = CHAPTER_TERRAIN_MAP[chapterId] || "industrial";
  const terrain = TERRAIN_THEMES[terrainKey];
  
  // Configurações de arena por capítulo
  const arenaConfigs: Record<number, { size: number; shape: ArenaShape; density: number }> = {
    1: { size: 35, shape: "square", density: 0.3 },
    2: { size: 40, shape: "square", density: 0.35 },
    3: { size: 45, shape: "circle", density: 0.35 },
    4: { size: 45, shape: "cross", density: 0.4 },
    5: { size: 50, shape: "maze", density: 0.45 },
    6: { size: 50, shape: "corridor", density: 0.4 },
    7: { size: 48, shape: "circle", density: 0.35 },
    8: { size: 50, shape: "cross", density: 0.4 },
    9: { size: 52, shape: "maze", density: 0.45 },
    10: { size: 55, shape: "circle", density: 0.4 },
  };
  
  const config = arenaConfigs[chapterId] || arenaConfigs[1];
  
  // Total de inimigos no capítulo (para referência)
  const waves = CHAPTER_WAVES[chapterId] || [];
  const totalEnemies = waves.reduce((sum, w) => sum + w.enemyCount + (w.isBossWave ? 1 : 0), 0);
  
  return {
    wave: chapterId,
    arenaSize: config.size,
    arenaShape: config.shape,
    terrain,
    enemyCount: totalEnemies,
    enemies: [], // Será preenchido por generateStoryEnemies
    obstaclesDensity: config.density,
    hasDestructibles: chapterId >= 2,
  };
}

// Gerar inimigos para uma onda específica do modo história
export function generateStoryEnemies(
  chapterId: number,
  waveIndex: number,
  obstacles: ObstacleData[],
  arenaSize: number
): TankData[] {
  const waves = CHAPTER_WAVES[chapterId];
  if (!waves || waveIndex >= waves.length) return [];
  
  const waveConfig = waves[waveIndex];
  const random = seededRandom(
    deterministicSeed("story_wave", chapterId, waveIndex, arenaSize, obstacles.length)
  );
  const boundary = arenaSize / 2 - 4;
  
  const enemies: TankData[] = [];
  const isIntroDroneWave = chapterId === 1 && waveIndex === 0;
  
  // Posições de spawn nos cantos e bordas
  const spawnPositions = [
    new THREE.Vector3(-boundary, 0, -boundary),
    new THREE.Vector3(boundary, 0, -boundary),
    new THREE.Vector3(-boundary, 0, boundary),
    new THREE.Vector3(boundary, 0, boundary),
    new THREE.Vector3(0, 0, -boundary),
    new THREE.Vector3(0, 0, boundary),
    new THREE.Vector3(-boundary, 0, 0),
    new THREE.Vector3(boundary, 0, 0),
  ];
  
  const isPositionBlocked = (pos: THREE.Vector3, checkRadius: number = 2): boolean => {
    for (const obs of obstacles) {
      const dx = Math.abs(pos.x - obs.position.x);
      const dz = Math.abs(pos.z - obs.position.z);
      const halfWidth = obs.size.x / 2 + checkRadius;
      const halfDepth = obs.size.z / 2 + checkRadius;
      if (dx < halfWidth && dz < halfDepth) {
        return true;
      }
    }
    return false;
  };
  
  const findSafeSpawn = (basePos: THREE.Vector3, attempts: number = 15): THREE.Vector3 => {
    for (let a = 0; a < attempts; a++) {
      const testPos = basePos.clone();
      testPos.x += (random() - 0.5) * 8;
      testPos.z += (random() - 0.5) * 8;
      
      // Manter dentro dos limites
      testPos.x = Math.max(-boundary, Math.min(boundary, testPos.x));
      testPos.z = Math.max(-boundary, Math.min(boundary, testPos.z));
      
      if (!isPositionBlocked(testPos)) {
        return testPos;
      }
    }
    return basePos.clone();
  };
  
  // Se é wave de boss, spawnar o boss
  if (waveConfig.isBossWave && waveConfig.bossConfig) {
    const boss = waveConfig.bossConfig;
    const bossPos = findSafeSpawn(new THREE.Vector3(0, 0, -boundary * 0.7));
    
    enemies.push({
      id: boss.id,
      position: bossPos,
      rotation: Math.PI, // Virado para o jogador
      health: boss.phases > 1 && boss.phaseConfigs ? boss.phaseConfigs[0].health : boss.health,
      maxHealth: boss.phases > 1 && boss.phaseConfigs ? boss.phaseConfigs[0].health : boss.health,
      color: boss.color,
      isPlayer: false,
      isAlive: true,
      name: boss.name,
      lastFireTime: 0,
      enemyType: "boss",
      speed: boss.speed,
      damage: boss.damage,
      fireRate: boss.fireRate,
      shotCount: boss.shotCount,
      size: boss.size,
      aiLevel: boss.aiLevel,
    });
    
    return enemies;
  }
  
  // Spawnar inimigos normais da wave
  const enemyCount = waveConfig.enemyCount;
  const difficultyMultiplier = 1 + (chapterId - 1) * 0.15;
  
  for (let i = 0; i < enemyCount; i++) {
    const posIndex = i % spawnPositions.length;
    const basePos = spawnPositions[posIndex];
    const pos = findSafeSpawn(basePos);
    
    // Variedade de tipos baseada no capítulo
    const typeRoll = random();
    let enemyType: "normal" | "heavy" | "fast" | "sniper" = "normal";
    let health = 60 * difficultyMultiplier;
    let speed = 6;
    let damage = 15 * difficultyMultiplier;
    let fireRate = 2500;
    let shotCount = 1;
    let size = 1;
    let color = "#ff3366";
    
    if (chapterId >= 3 && typeRoll > 0.7) {
      // Heavy tank
      enemyType = "heavy";
      health = 100 * difficultyMultiplier;
      speed = 5;
      damage = 25 * difficultyMultiplier;
      fireRate = 2800;
      shotCount = 2;
      size = 1.3;
      color = "#9933ff";
    } else if (chapterId >= 2 && typeRoll > 0.5) {
      // Fast tank
      enemyType = "fast";
      health = 40 * difficultyMultiplier;
      speed = 10;
      damage = 10 * difficultyMultiplier;
      fireRate = 2000;
      shotCount = 1;
      size = 0.85;
      color = "#33ff99";
    } else if (chapterId >= 4 && typeRoll > 0.35) {
      // Sniper tank
      enemyType = "sniper";
      health = 50 * difficultyMultiplier;
      speed = 4;
      damage = 30 * difficultyMultiplier;
      fireRate = 3500;
      shotCount = 1;
      size = 1.1;
      color = "#00ccff";
    }
    
    enemies.push({
      id: `enemy-${waveIndex}-${i}`,
      position: pos,
      rotation: random() * Math.PI * 2,
      health: Math.round(health),
      maxHealth: Math.round(health),
      color,
      isPlayer: false,
      isAlive: true,
      name: `Drone ${waveIndex + 1}-${i + 1}`,
      lastFireTime: 0,
      enemyType,
      visualType: isIntroDroneWave ? "drone" : "tank",
      speed,
      damage: Math.round(damage),
      fireRate,
      shotCount,
      size,
      aiLevel: 0.4 + chapterId * 0.08,
    });
  }
  
  return enemies;
}

// Obter informações da wave atual
export function getStoryWaveInfo(chapterId: number, waveIndex: number): StoryWaveConfig | null {
  const waves = CHAPTER_WAVES[chapterId];
  if (!waves || waveIndex >= waves.length) return null;
  return waves[waveIndex];
}

// Obter total de waves de um capítulo
export function getTotalWaves(chapterId: number): number {
  return CHAPTER_WAVES[chapterId]?.length || 0;
}

// Verificar se é a última wave (boss wave)
export function isBossWave(chapterId: number, waveIndex: number): boolean {
  const waveInfo = getStoryWaveInfo(chapterId, waveIndex);
  return waveInfo?.isBossWave || false;
}

// Obter boss config para um capítulo
export function getBossConfig(chapterId: number): BossConfig | null {
  return BOSS_CONFIGS[chapterId] || null;
}

// ============================================
// DUNGEON ROOM ENEMY GENERATION
// ============================================

import { DungeonRoom, RoomWaveConfig, EnemyType } from "./dungeonTypes";

/**
 * Configuração de stats base por tipo de inimigo
 */
const ENEMY_TYPE_STATS: Record<EnemyType, Partial<TankData>> = {
  normal: {
    health: 70,
    maxHealth: 70,
    speed: 2.8,
    damage: 9,
    fireRate: 2400,
    color: "#ff5a7a",
    size: 1,
    aiLevel: 0.34,
    enemyType: "normal",
  },
  fast: {
    health: 52,
    maxHealth: 52,
    speed: 4.4,
    damage: 8,
    fireRate: 2800,
    color: "#22c55e",
    size: 0.85,
    aiLevel: 0.5,
    enemyType: "fast",
  },
  heavy: {
    health: 120,
    maxHealth: 120,
    speed: 2,
    damage: 13,
    fireRate: 3000,
    color: "#a855f7",
    size: 1.3,
    aiLevel: 0.36,
    enemyType: "heavy",
  },
  sniper: {
    health: 58,
    maxHealth: 58,
    speed: 2.4,
    damage: 16,
    fireRate: 3400,
    color: "#38bdf8",
    size: 0.9,
    aiLevel: 0.58,
    enemyType: "sniper",
  },
  kamikaze: {
    health: 45,
    maxHealth: 45,
    speed: 5.8,
    damage: 24,
    fireRate: 3600,
    color: "#ff8800",
    size: 0.8,
    aiLevel: 0.42,
    enemyType: "normal", // kamikaze usa comportamento especial via aiLevel
  },
  boss: {
    health: 500,
    maxHealth: 500,
    speed: 2.5,
    damage: 30,
    fireRate: 1200,
    color: "#ff0000",
    size: 1.5,
    aiLevel: 0.95,
    enemyType: "boss",
  },
};

/**
 * Gerar inimigos para uma sala de dungeon
 */
export function generateDungeonRoomEnemies(
  room: DungeonRoom,
  _waveIndex: number,
  obstacles: ObstacleData[],
  difficultyMultiplier: number = 1
): TankData[] {
  // Salas sem combate
  if (room.type === "corridor" || room.type === "rest" || room.type === "treasure") {
    return [];
  }
  
  // Sem configuração de combate
  if (!room.waves || room.waves.length === 0) {
    return [];
  }
  
  const random = seededRandom(
    deterministicSeed("dungeon_room", room.id, room.arenaSize, room.waves.length)
  );
  const boundary = room.arenaSize / 2 - 4;
  
  const enemies: TankData[] = [];
  const isDroneRoom = room.id === "ch1-tutorial-1";
  const roomWaves = room.waves;
  
  // Posições de spawn
  const spawnPositions = [
    new THREE.Vector3(-boundary, 0, -boundary),
    new THREE.Vector3(boundary, 0, -boundary),
    new THREE.Vector3(-boundary, 0, boundary * 0.5),
    new THREE.Vector3(boundary, 0, boundary * 0.5),
    new THREE.Vector3(0, 0, -boundary),
    new THREE.Vector3(-boundary * 0.5, 0, 0),
    new THREE.Vector3(boundary * 0.5, 0, 0),
    new THREE.Vector3(0, 0, -boundary * 0.5),
  ];
  
  const isPositionBlocked = (pos: THREE.Vector3, checkRadius: number = 2): boolean => {
    for (const obs of obstacles) {
      const dx = Math.abs(pos.x - obs.position.x);
      const dz = Math.abs(pos.z - obs.position.z);
      const halfWidth = obs.size.x / 2 + checkRadius;
      const halfDepth = obs.size.z / 2 + checkRadius;
      if (dx < halfWidth && dz < halfDepth) {
        return true;
      }
    }
    return false;
  };
  
  const findSafeSpawn = (basePos: THREE.Vector3, attempts: number = 15): THREE.Vector3 => {
    for (let a = 0; a < attempts; a++) {
      const testPos = basePos.clone();
      testPos.x += (random() - 0.5) * 8;
      testPos.z += (random() - 0.5) * 8;
      testPos.x = Math.max(-boundary, Math.min(boundary, testPos.x));
      testPos.z = Math.max(-boundary, Math.min(boundary, testPos.z));
      
      if (!isPositionBlocked(testPos)) {
        return testPos;
      }
    }
    return basePos.clone();
  };
  
  // Boss room: o boss aparece imediatamente ao entrar na sala final.
  const bossWave = roomWaves.find((wave) => !!wave.bossConfig);
  if (bossWave?.bossConfig) {
    const boss = bossWave.bossConfig;
    const bossPos = findSafeSpawn(new THREE.Vector3(0, 0, -boundary * 0.7));
    
    enemies.push({
      id: boss.id,
      position: bossPos,
      rotation: Math.PI,
      health: boss.phases > 1 && boss.phaseConfigs ? boss.phaseConfigs[0].health : boss.health,
      maxHealth: boss.phases > 1 && boss.phaseConfigs ? boss.phaseConfigs[0].health : boss.health,
      color: boss.color,
      isPlayer: false,
      isAlive: true,
      name: boss.name,
      lastFireTime: 0,
      enemyType: "boss",
      speed: boss.speed,
      damage: boss.damage,
      fireRate: boss.fireRate,
      shotCount: boss.shotCount,
      size: boss.size,
      aiLevel: boss.aiLevel,
    });
    return enemies;
  }
  
  // Salas de combate: todos os inimigos da sala são carregados de uma vez.
  // NOTA: Quando multi-wave spawning estiver ativo, use generateDungeonSingleWaveEnemies()
  // para spawnar waves individualmente com delay.
  for (let waveIndex = 0; waveIndex < roomWaves.length; waveIndex++) {
    const waveConfig = roomWaves[waveIndex];
    const enemyCount = waveConfig.enemyCount;
    const enemyTypes: EnemyType[] =
      waveConfig.enemyTypes.length > 0 ? waveConfig.enemyTypes : ["normal"];
    const diffMult = (waveConfig.difficultyMultiplier || 1) * difficultyMultiplier;
    
    for (let i = 0; i < enemyCount; i++) {
      const spawnPos = spawnPositions[enemies.length % spawnPositions.length];
      const position = findSafeSpawn(spawnPos);
      
      // Escolher tipo aleatório
      const type = enemyTypes[Math.floor(random() * enemyTypes.length)];
      const baseStats = ENEMY_TYPE_STATS[type];
      
      enemies.push({
        id: `dungeon-enemy-${room.id}-w${waveIndex}-${i}`,
        position,
        rotation: Math.PI,
        health: Math.round((baseStats.health || 80) * diffMult),
        maxHealth: Math.round((baseStats.maxHealth || 80) * diffMult),
        color: baseStats.color || "#666666",
        isPlayer: false,
        isAlive: true,
        name: `${type.charAt(0).toUpperCase() + type.slice(1)} ${isDroneRoom ? "Drone" : "Tank"}`,
        lastFireTime: 0,
        enemyType: baseStats.enemyType,
        visualType: isDroneRoom ? "drone" : "tank",
        speed: Math.max(1.2, baseStats.speed ?? 3),
        damage: Math.round((baseStats.damage || 15) * diffMult),
        fireRate: Math.max(600, baseStats.fireRate ?? 1800),
        size: baseStats.size || 1,
        aiLevel: Math.max(0.1, Math.min(1, baseStats.aiLevel ?? 0.4)),
      });
    }
  }
  
  return enemies;
}

/**
 * Obter o total de inimigos de uma sala (todas as waves)
 */
export function getDungeonRoomTotalEnemies(room: DungeonRoom): number {
  if (!room.waves) return 0;
  return room.waves.reduce((total, wave) => {
    if (wave.enemyCount > 0) return total + wave.enemyCount;
    if (wave.bossConfig) return total + 1;
    return total;
  }, 0);
}

/**
 * Obter configuração de arena para uma sala de dungeon
 */
export function getDungeonRoomArenaConfig(room: DungeonRoom): WaveConfig {
  return {
    wave: 1,
    enemyCount: room.waves?.[0]?.enemyCount || 0,
    arenaSize: room.arenaSize,
    arenaShape: room.arenaShape,
    terrain: room.terrainTheme,
    obstaclesDensity: room.obstaclesDensity ?? 5,
    hasDestructibles: true,
    enemies: [],
  };
}

/**
 * Gerar inimigos para uma ÚNICA wave de uma sala de dungeon.
 * Usado pelo sistema de wave-staggering para spawnar waves com delay.
 */
export function generateDungeonSingleWaveEnemies(
  room: DungeonRoom,
  waveIndex: number,
  obstacles: ObstacleData[],
  difficultyMultiplier: number = 1
): TankData[] {
  if (!room.waves || waveIndex >= room.waves.length) return [];

  const waveConfig = room.waves[waveIndex];
  const random = seededRandom(
    deterministicSeed("dungeon_wave", room.id, waveIndex, room.arenaSize, room.waves.length)
  );
  const boundary = room.arenaSize / 2 - 4;

  const spawnPositions = [
    new THREE.Vector3(-boundary, 0, -boundary),
    new THREE.Vector3(boundary, 0, -boundary),
    new THREE.Vector3(-boundary, 0, boundary * 0.5),
    new THREE.Vector3(boundary, 0, boundary * 0.5),
    new THREE.Vector3(0, 0, -boundary),
    new THREE.Vector3(-boundary * 0.5, 0, 0),
    new THREE.Vector3(boundary * 0.5, 0, 0),
    new THREE.Vector3(0, 0, -boundary * 0.5),
  ];

  const isPositionBlocked = (pos: THREE.Vector3, checkRadius: number = 2): boolean => {
    for (const obs of obstacles) {
      const dx = Math.abs(pos.x - obs.position.x);
      const dz = Math.abs(pos.z - obs.position.z);
      const halfWidth = obs.size.x / 2 + checkRadius;
      const halfDepth = obs.size.z / 2 + checkRadius;
      if (dx < halfWidth && dz < halfDepth) return true;
    }
    return false;
  };

  const findSafeSpawn = (basePos: THREE.Vector3, attempts: number = 15): THREE.Vector3 => {
    for (let a = 0; a < attempts; a++) {
      const testPos = basePos.clone();
      testPos.x += (random() - 0.5) * 8;
      testPos.z += (random() - 0.5) * 8;
      testPos.x = Math.max(-boundary, Math.min(boundary, testPos.x));
      testPos.z = Math.max(-boundary, Math.min(boundary, testPos.z));
      if (!isPositionBlocked(testPos)) return testPos;
    }
    return basePos.clone();
  };

  const enemies: TankData[] = [];
  const isDroneRoom = room.id === "ch1-tutorial-1";
  const enemyCount = waveConfig.enemyCount;
  const enemyTypes: EnemyType[] =
    waveConfig.enemyTypes.length > 0 ? waveConfig.enemyTypes : ["normal"];
  const diffMult = (waveConfig.difficultyMultiplier || 1) * difficultyMultiplier;

  for (let i = 0; i < enemyCount; i++) {
    const spawnPos = spawnPositions[i % spawnPositions.length];
    const position = findSafeSpawn(spawnPos);
    const type = enemyTypes[Math.floor(random() * enemyTypes.length)];
    const baseStats = ENEMY_TYPE_STATS[type];

    enemies.push({
      id: `dungeon-enemy-${room.id}-w${waveIndex}-${i}`,
      position,
      rotation: Math.PI,
      health: Math.round((baseStats.health || 80) * diffMult),
      maxHealth: Math.round((baseStats.maxHealth || 80) * diffMult),
      color: baseStats.color || "#666666",
      isPlayer: false,
      isAlive: true,
      name: `${type.charAt(0).toUpperCase() + type.slice(1)} ${isDroneRoom ? "Drone" : "Tank"}`,
      lastFireTime: 0,
      enemyType: baseStats.enemyType,
      visualType: isDroneRoom ? "drone" : "tank",
      speed: baseStats.speed || 3,
      damage: Math.round((baseStats.damage || 15) * diffMult),
      fireRate: baseStats.fireRate || 1,
      size: baseStats.size || 1,
      aiLevel: baseStats.aiLevel || 2,
    });
  }

  return enemies;
}

/**
 * Obter delayMs para uma wave específica (tempo de espera antes de spawnar)
 */
export function getDungeonWaveDelay(room: DungeonRoom, waveIndex: number): number {
  if (!room.waves || waveIndex >= room.waves.length) return 0;
  return room.waves[waveIndex].delayMs ?? 0;
}

/**
 * Obter o número total de waves em uma sala
 */
export function getDungeonRoomWaveCount(room: DungeonRoom): number {
  return room.waves?.length || 0;
}


